import {
  call,
  put,
  select,
  takeLatest,
  delay,
  fork,
  cancel,
  debounce,
} from 'redux-saga/effects';
import { notification } from 'antd';
import { setActiveBranch } from 'modules/SalaryModule/CompanySettings/actions';
import { downloadFile } from 'helpers/common';
import { selectActiveBranchData } from 'modules/SalaryModule/CompanySettings/selectors';
import { selectUserCurrentCompany } from 'modules/loggedUserInfo/selectors';
import {
  getEmployeesWithAbsences,
  downloadEmployeeTableXLSX,
  getEmployees,
  setEmployeeSchedule,
  fetchDownloadEmployeePayslips,
  fetchDownloadCompanyEmployeesPayslips,
  fetchDownloadCompanyEmployeesPdf,
  fetchDownloadCompanyEmployeesPayslipsZip,
} from 'modules/SalaryModule/Employees/services';
import {
  CHANGE_EMPLOYEES_FILTERS,
  DOWNLOAD_EMPLOYEES_TABLE_XLSX,
  FETCH_COMPANY_BRANCHES_REQUEST,
  FETCH_EMPLOYEES_REQUEST,
  fetchCompanyBranchesSuccess,
  fetchEmployeesError,
  fetchEmployeesSuccess,
  SET_EMPLOYEE_SCHEDULE,
  setEmployeesFilters,
  setEmployeeScheduleSuccess,
  DOWNLOAD_EMPLOYEE_PAYSLIPS,
  DOWNLOAD_COMPANY_EMPLOYEES_PAYSLIPS,
  downloadCompanyEmployeesPayslipsSuccess,
  downloadEmployeePayslipsSuccess,
  fetchLastSyncTimeSuccess,
  fetchIsSyncingSuccess,
  REFRESH_SILAE_EMPLOYEES,
  fetchCompanyBranchesRequest,
  FETCH_EMPLOYEES_DEBOUNCED_REQUEST,
  setEmployeesPagePagination,
  setAllEmployees,
  fetchEmployeesRequest,
} from 'modules/SalaryModule/Employees/actions';
import {
  selectDateRange,
  selectFilters,
  selectEmployeePagination,
} from 'modules/SalaryModule/Employees/selectors';
import {
  selectCalendarDateFilter,
  selectCalendarBranchFilter,
  selectCalendarPagination,
} from 'modules/SalaryModule/Calendar/selectors';
import { setCalendarPagePagination } from 'modules/SalaryModule/Calendar/actions';
import {
  SILAE_API_ERRORS,
  SILAE_WARNINGS,
} from 'const/translations/SalaryEmployeePage';

import { EmployeePageType } from 'const/SalaryMainPage';
import {
  getCompanyBranches,
  refreshSilaeEmployeesRequest,
  fetchCompanySynchronizationRequest,
} from '../../CompanySettings/services';

import { getCsvDownloadQuery } from '../utils';

const { NO_DATA_EMPLOYEES, NO_DATA_EMPLOYEE } = SILAE_API_ERRORS;
const { EMPLOYEE_TIMEOUT } = SILAE_WARNINGS;

function* showTimeoutWarning() {
  yield delay(5000);
  yield call(notification.warning, {
    message: EMPLOYEE_TIMEOUT,
    duration: 10,
  });
}

function* fetchEmployeesWorker({
  payload: { pageType, isYearCalendar, search },
}) {
  const timeoutTask = yield fork(showTimeoutWarning);
  const { id: companyId, managedSilae } = yield select(
    selectUserCurrentCompany
  );
  //calendar
  const calendarDateFilter = yield select(selectCalendarDateFilter);
  const calendarBranchFilter = yield select(selectCalendarBranchFilter);
  const calendarPagination = yield select(selectCalendarPagination);

  //employees
  const dateRange = yield select(selectDateRange);
  const filters = yield select(selectFilters);
  const employeePagination = yield select(selectEmployeePagination);

  const payloadForCalendar = {
    companyId,
    startDate: isYearCalendar
      ? calendarDateFilter.startOf('year').format('YYYY-MM-DD')
      : calendarDateFilter.format('YYYY-MM-DD'),
    endDate: isYearCalendar
      ? calendarDateFilter.endOf('year').format('YYYY-MM-DD')
      : dateRange.endDate.format('YYYY-MM-DD'),
    siret: calendarBranchFilter || undefined,
    name: search?.trim() || undefined,
    page: calendarPagination.currentPage,
    limit: calendarPagination.limit,
  };

  const payloadForEmployee = {
    companyId,
    startDate: dateRange.startDate.format('YYYY-MM-DD'),
    endDate: dateRange.endDate.format('YYYY-MM-DD'),
    siret: filters.establishments || undefined,
    position: filters.natureEmployments || undefined,
    classification: filters.classifications || undefined,
    contract: filters.contracts || undefined,
    name: search?.trim() || undefined,
    page: employeePagination.currentPage,
    limit: employeePagination.limit,
  };

  const payload =
    pageType === EmployeePageType.Calendar
      ? payloadForCalendar
      : payloadForEmployee;

  try {
    const { data: sync } = yield call(getEmployees, {
      companyId,
      startDate:
        calendarDateFilter.format('YYYY-MM-DD') ||
        dateRange.startDate.format('YYYY-MM-DD'),
    });
    const { data, meta } = yield call(getEmployeesWithAbsences, payload);

    if (pageType === EmployeePageType.Calendar) {
      yield put(setCalendarPagePagination(meta));
    }

    if (pageType === EmployeePageType.Employees) {
      yield put(setEmployeesPagePagination(meta));
    }

    yield cancel(timeoutTask);

    yield put(fetchEmployeesSuccess({ data: data.employees }));
    yield put(setAllEmployees(sync.employees));

    yield put(fetchLastSyncTimeSuccess({ data: sync.lastSyncTime }));
    yield put(fetchIsSyncingSuccess({ data: sync.lastSyncingStatus }));

    if (managedSilae && sync.lastSyncingStatus === 'in_process') {
      yield call(
        checkSilaeEmployeesWorker,
        payload.companyId,
        payload.dateStart
      );
    }
  } catch (error) {
    yield cancel(timeoutTask);
    yield put(fetchEmployeesError({ error }));
  }
}

function* watchDebouncedFetchEmployeesWorker() {
  yield debounce(300, FETCH_EMPLOYEES_DEBOUNCED_REQUEST, fetchEmployeesWorker);
}

function* fetchCompanyBranchesWorker({ payload }) {
  try {
    const { data } = yield call(getCompanyBranches, payload);
    yield put(fetchCompanyBranchesSuccess(data));
    const activeBranch = yield select(selectActiveBranchData);
    if (data.length === 0) {
      yield put(setActiveBranch(null));
    }
    const shouldUpdateActiveBranch =
      !activeBranch ||
      (activeBranch && !data.some(({ siret }) => siret === activeBranch.siret));
    if (shouldUpdateActiveBranch) {
      yield put(setActiveBranch(data[0]));
    }
  } catch (error) {
    yield put(fetchEmployeesError({ error }));
  }
}

function* changeEmployeesFiltersWorker({ payload }) {
  try {
    const selectedFilters = yield select(selectFilters);
    const filters = { ...selectedFilters };
    Object.keys(payload).forEach((key) => {
      filters[key] = payload[key];
    });

    yield put(setEmployeesFilters(filters));
  } catch (error) {
    yield put(fetchEmployeesError({ error }));
  }
}

function* setEmployeeScheduleWorker({ payload }) {
  try {
    const newSchedule = yield call(setEmployeeSchedule, payload);
    yield put(setEmployeeScheduleSuccess({ newSchedule }));
  } catch (error) {
    yield put(fetchEmployeesError({ error }));
  }
}

function* downloadEmployeeTableXLSXWorker({ payload: { companyId, filter } }) {
  try {
    const query = getCsvDownloadQuery(filter);

    const { fileData, fileName } = yield call(downloadEmployeeTableXLSX, {
      companyId,
      query,
    });

    yield call(downloadFile, fileData, fileName);
  } catch (error) {
    yield put(fetchEmployeesError({ error }));
  }
}

function* downloadEmployeePayslipsHandler({
  payload: { companyId, employeeId, startDate, endDate },
}) {
  try {
    const { fileData, fileName } = yield call(fetchDownloadEmployeePayslips, {
      companyId,
      employeeId,
      startDate,
      endDate,
    });

    yield call(downloadFile, fileData, fileName);
    yield put(downloadEmployeePayslipsSuccess());
  } catch (error) {
    console.warn(error);

    notification.error({
      message: NO_DATA_EMPLOYEE,
    });
    yield put(downloadEmployeePayslipsSuccess());
  }
}

function* downloadCompanyEmployeesPayslipsHandler({
  payload: { companyId, startDate, endDate, key, format },
}) {
  try {
    if (format === 'zip') {
      const { fileData, fileName } = yield call(
        fetchDownloadCompanyEmployeesPayslipsZip,
        {
          companyId,
          startDate,
          endDate,
        }
      );

      yield call(downloadFile, fileData, fileName);
      yield put(downloadCompanyEmployeesPayslipsSuccess());

      return;
    }

    const { fileData, fileName } = yield !key
      ? call(fetchDownloadCompanyEmployeesPayslips, {
          companyId,
          startDate,
          endDate,
        })
      : call(fetchDownloadCompanyEmployeesPdf, {
          companyId,
          startDate,
          endDate,
          key,
        });

    yield call(downloadFile, fileData, fileName);
    yield put(downloadCompanyEmployeesPayslipsSuccess());
  } catch (error) {
    console.warn(error);

    notification.error({
      message: NO_DATA_EMPLOYEES,
    });
    yield put(downloadCompanyEmployeesPayslipsSuccess());
  }
}

function* refreshSilaeEmployeesWorker({ payload: { companyId, dateRange } }) {
  try {
    const { data } = yield call(refreshSilaeEmployeesRequest, companyId);

    yield put(fetchIsSyncingSuccess({ data: data.lastSyncingStatus }));

    yield call(
      checkSilaeEmployeesWorker,
      companyId,
      dateRange.startDate.format('YYYY-MM-DD')
    );
  } catch (error) {
    notification.error({
      message: error.error,
    });
  }
}

function* checkSilaeEmployeesWorker(companyId, startDate) {
  try {
    yield delay(5000);
    const { data } = yield call(fetchCompanySynchronizationRequest, companyId);

    if (data.lastSyncingStatus === 'in_process') {
      yield call(checkSilaeEmployeesWorker, companyId, startDate);

      return;
    }

    yield put(fetchIsSyncingSuccess({ data: data.lastSyncingStatus }));

    if (data.lastSyncingStatus === 'failed') {
      notification.error({
        message: 'Erreur synchro',
      });

      return;
    }

    if (data.lastSyncingStatus === 'success') {
      yield put(fetchLastSyncTimeSuccess({ data: data.lastSyncTime }));
    }

    yield put(fetchCompanyBranchesRequest(companyId));
    yield put(fetchEmployeesRequest({ pageType: EmployeePageType.Employees }));
  } catch (error) {
    console.error(error);
    notification.error({
      message: 'une erreur s’est produite, merci de contacter le support',
    });
  }
}

export function* employeesMainSaga() {
  yield takeLatest(FETCH_EMPLOYEES_REQUEST, fetchEmployeesWorker);
  yield takeLatest(CHANGE_EMPLOYEES_FILTERS, changeEmployeesFiltersWorker);
  yield takeLatest(SET_EMPLOYEE_SCHEDULE, setEmployeeScheduleWorker);
  yield takeLatest(FETCH_COMPANY_BRANCHES_REQUEST, fetchCompanyBranchesWorker);
  yield takeLatest(
    DOWNLOAD_EMPLOYEES_TABLE_XLSX,
    downloadEmployeeTableXLSXWorker
  );
  yield takeLatest(DOWNLOAD_EMPLOYEE_PAYSLIPS, downloadEmployeePayslipsHandler);
  yield takeLatest(
    DOWNLOAD_COMPANY_EMPLOYEES_PAYSLIPS,
    downloadCompanyEmployeesPayslipsHandler
  );
  yield takeLatest(REFRESH_SILAE_EMPLOYEES, refreshSilaeEmployeesWorker);
  yield fork(watchDebouncedFetchEmployeesWorker);
}
